// Full recalibration wizard — 4 movements, 8–12 minutes.
// Monthly ritual for paid tiers. Runs from day 28 onward (bi-weekly for Coach).
//
// Source: CogniPrint_Recalibration_Cadence_Research_v2.md §4,
// CogniPrint_Recalibration_Dev_Plan_v1.md §3.7 (REC-030).

import { useEffect, useState } from 'react';
import { X, Check, CalendarPlus } from 'lucide-react';
import { googleCalendarUrl, daysFromNowAt10 } from '../../lib/calendar';
import {
  startFullRecalibration,
  completeFullRecalibration,
  previewCounterfactual,
  type FullPlan,
  type DimensionChange,
} from '@shared/services/recalibration-api';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';
import { recordGamificationAction } from '@shared/services/gamification';
import { useAuthStore, useSubscriptionStore } from '@shared/state';
import { Reflection } from './movements/Reflection';
import { DimensionConfirm } from './movements/DimensionConfirm';
import { AccuracyCheck } from './movements/AccuracyCheck';
import { SuggestionBox } from './movements/SuggestionBox';

interface Props {
  open: boolean;
  softReset?: boolean;
  onClose: () => void;
}

type Phase =
  | 'loading'
  | 'reflection'
  | 'dimensions'
  | 'accuracy'
  | 'suggestion'
  | 'submitting'
  | 'results'
  | 'error';

interface Answers {
  reflection: { chipSelections: string[]; freeText: string | null };
  dimensionConfirmations: Record<
    string,
    { userResponse: string; freeText: string | null }
  >;
  accuracyCheck: { chipSelections: string[]; freeText: string | null };
  suggestion: { freeText: string | null };
}

const DEFAULT_ANSWERS: Answers = {
  reflection: { chipSelections: [], freeText: null },
  dimensionConfirmations: {},
  accuracyCheck: { chipSelections: [], freeText: null },
  suggestion: { freeText: null },
};

export function FullWizard({ open, softReset, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [plan, setPlan] = useState<FullPlan | null>(null);
  const [answers, setAnswers] = useState<Answers>(DEFAULT_ANSWERS);
  const [dimIndex, setDimIndex] = useState(0);
  const [summary, setSummary] = useState('');
  const [changes, setChanges] = useState<DimensionChange[]>([]);
  const [cycleDays, setCycleDays] = useState(28);
  // Must run before the `if (!open) return null` guard below — calling a hook
  // after a conditional early return violates the Rules of Hooks and throws
  // "rendered more hooks than during the previous render" when `open` toggles.
  const subscription = useSubscriptionStore((s) => s.subscription);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function start() {
      setPhase('loading');
      setError(null);
      setAnswers(DEFAULT_ANSWERS);
      setDimIndex(0);
      try {
        const result = await startFullRecalibration();
        if (cancelled) return;
        setSessionId(result.sessionId);
        setPlan(result.plan);
        trackEvent({
          name: ANALYTICS_EVENTS.RECAL_FULL_STARTED,
          properties: { ...getBaseProperties(), cycleDays: 28 },
        });
        setPhase('reflection');
      } catch (err) {
        if (cancelled) return;
        setPhase('error');
        setError(err instanceof Error ? err.message : 'Unexpected error');
      }
    }

    void start();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  function toggleChip(
    group: 'reflection' | 'accuracyCheck',
    chip: string,
  ) {
    setAnswers((prev) => {
      const existing = prev[group];
      const chipSelections = existing.chipSelections.includes(chip)
        ? existing.chipSelections.filter((c) => c !== chip)
        : [...existing.chipSelections, chip];
      return { ...prev, [group]: { ...existing, chipSelections } };
    });
  }

  function setGroupFreeText(
    group: 'reflection' | 'accuracyCheck' | 'suggestion',
    text: string,
  ) {
    setAnswers((prev) => ({
      ...prev,
      [group]: {
        ...(prev[group] as { chipSelections?: string[]; freeText: string | null }),
        freeText: text.trim() || null,
      },
    }));
  }

  function setDimAnswer(
    dimension: string,
    patch: Partial<{ userResponse: string; freeText: string | null }>,
  ) {
    setAnswers((prev) => ({
      ...prev,
      dimensionConfirmations: {
        ...prev.dimensionConfirmations,
        [dimension]: {
          userResponse: patch.userResponse ?? prev.dimensionConfirmations[dimension]?.userResponse ?? '',
          freeText:
            patch.freeText !== undefined
              ? patch.freeText
              : prev.dimensionConfirmations[dimension]?.freeText ?? null,
        },
      },
    }));
  }

  async function onSubmit() {
    if (!sessionId || !plan) return;
    setPhase('submitting');
    try {
      const dimensionConfirmations = plan.dimension_confirmations.map((d) => {
        const a = answers.dimensionConfirmations[d.dimension];
        return {
          dimension: d.dimension,
          userResponse: a?.userResponse ?? 'Yes',
          freeText: a?.freeText ?? null,
        };
      });

      const result = await completeFullRecalibration({
        sessionId,
        reflection: answers.reflection,
        dimensionConfirmations,
        accuracyCheck: answers.accuracyCheck,
        suggestion: answers.suggestion,
        softReset,
      });

      setSummary(result.overallSummary);
      setChanges(result.dimensionChanges);
      setCycleDays(result.cycleDays);
      trackEvent({
        name: ANALYTICS_EVENTS.RECAL_FULL_COMPLETED,
        properties: {
          ...getBaseProperties(),
          durationMs: 0,
          dimensionsShifted: result.dimensionChanges.length,
        },
      });
      if (softReset) {
        trackEvent({
          name: ANALYTICS_EVENTS.RECAL_SOFT_RESET_ACCEPTED,
          properties: { ...getBaseProperties(), daysOverdue: 14 },
        });
      }
      const viewer = useAuthStore.getState().user;
      if (viewer) {
        const tierAllowsFreezes = viewer.subscription === 'pro' || viewer.subscription === 'teams';
        void recordGamificationAction(viewer.uid, 'recalibration_completed', {
          counterMutations: { fullRecalibrationsCompleted: 1 },
          tickStreak: true,
          tierAllowsFreezes,
        });
      }
      setPhase('results');
    } catch (err) {
      setPhase('error');
      setError(err instanceof Error ? err.message : 'Could not save recalibration');
    }
  }

  const activeDim = plan?.dimension_confirmations[dimIndex];
  const activeDimAnswer = activeDim
    ? answers.dimensionConfirmations[activeDim.dimension]
    : null;

  const hasCoach =
    subscription?.addOns?.coach === true ||
    subscription?.teamCoachAssignment?.enabled === true;

  async function fetchCounterfactual(alternative: string): Promise<string> {
    if (!activeDim) return '';
    try {
      const { sentence } = await previewCounterfactual({
        dimension: activeDim.dimension,
        currentLabel: activeDim.current_label,
        alternativeLabel: alternative,
      });
      return sentence;
    } catch {
      return '';
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      <div
        className="absolute inset-0"
        onClick={onClose}
        style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
      />
      <div
        className="relative w-full max-w-xl rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #111528, #0B0E1A)',
          border: '1px solid #2A2F4F',
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-content-muted hover:text-content transition-colors"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <X size={15} />
        </button>

        <div style={{ padding: '30px 28px' }}>
          {phase === 'loading' && (
            <div className="py-10 text-center text-content-muted text-sm">
              Preparing your recalibration…
            </div>
          )}

          {phase === 'error' && (
            <div className="py-10 text-center">
              <p className="text-red-400 text-sm mb-4">{error}</p>
              <button onClick={onClose} className="text-content-muted underline text-sm">
                Close
              </button>
            </div>
          )}

          {phase === 'reflection' && plan && (
            <Reflection
              question={plan.reflection.text}
              chips={plan.reflection.chips}
              chipSelections={answers.reflection.chipSelections}
              freeText={answers.reflection.freeText}
              onToggleChip={(chip) => toggleChip('reflection', chip)}
              onFreeTextChange={(text) => setGroupFreeText('reflection', text)}
              onContinue={() => setPhase('dimensions')}
            />
          )}

          {phase === 'dimensions' && activeDim && plan && (
            <DimensionConfirm
              index={dimIndex}
              total={plan.dimension_confirmations.length}
              question={activeDim.text}
              currentLabel={activeDim.current_label}
              currentScore={activeDim.current_score}
              chips={activeDim.chips}
              userResponse={activeDimAnswer?.userResponse ?? null}
              freeText={activeDimAnswer?.freeText ?? null}
              onSelectChip={(chip) => setDimAnswer(activeDim.dimension, { userResponse: chip })}
              onFreeTextChange={(text) => setDimAnswer(activeDim.dimension, { freeText: text })}
              onBack={() => {
                if (dimIndex === 0) setPhase('reflection');
                else setDimIndex(dimIndex - 1);
              }}
              onNext={() => {
                if (dimIndex < plan.dimension_confirmations.length - 1) {
                  setDimIndex(dimIndex + 1);
                } else {
                  setPhase('accuracy');
                }
              }}
              onHoverChip={hasCoach ? fetchCounterfactual : undefined}
            />
          )}

          {phase === 'accuracy' && plan && (
            <AccuracyCheck
              question={plan.accuracy_check.text}
              chips={plan.accuracy_check.chips}
              chipSelections={answers.accuracyCheck.chipSelections}
              freeText={answers.accuracyCheck.freeText}
              onToggleChip={(chip) => toggleChip('accuracyCheck', chip)}
              onFreeTextChange={(text) => setGroupFreeText('accuracyCheck', text)}
              onBack={() => setPhase('dimensions')}
              onContinue={() => setPhase('suggestion')}
            />
          )}

          {phase === 'suggestion' && plan && (
            <SuggestionBox
              question={plan.suggestion.text}
              freeText={answers.suggestion.freeText}
              softReset={softReset}
              onFreeTextChange={(text) => setGroupFreeText('suggestion', text)}
              onBack={() => setPhase('accuracy')}
              onSubmit={onSubmit}
            />
          )}

          {phase === 'submitting' && (
            <div className="py-10 text-center text-content-muted text-sm">
              Applying your recalibration…
            </div>
          )}

          {/* Results */}
          {phase === 'results' && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-gold-500 font-semibold mb-1">
                {softReset ? 'Cycle re-anchored' : 'Recalibration complete'}
              </div>
              {softReset && (
                <p className="text-content-secondary text-sm mb-3 leading-relaxed">
                  We’ve set your next recalibration for {cycleDays} days from today. You can change this in Settings.
                </p>
              )}
              <p className="text-content-secondary text-sm mb-4 leading-relaxed">
                {summary}
              </p>
              <div
                className="rounded-lg p-3 mb-4"
                style={{
                  background: 'rgba(108,92,231,0.06)',
                  border: '1px solid rgba(108,92,231,0.18)',
                }}
              >
                <div className="text-[11px] uppercase tracking-wider text-indigo-300 font-semibold mb-1">
                  Next recalibration
                </div>
                <div className="text-[13px] text-white">
                  {cycleDays} days from today
                </div>
                {cycleDays > 0 && (
                  <a
                    href={googleCalendarUrl({
                      title: 'CogniPrint recalibration',
                      details:
                        'Time to recalibrate your CogniPrint learning profile — about 10 minutes.',
                      start: daysFromNowAt10(cycleDays),
                      durationMinutes: 15,
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-[12px] font-medium text-indigo-300 hover:text-indigo-200 transition-colors"
                  >
                    <CalendarPlus size={13} /> Add to calendar
                  </a>
                )}
              </div>

              <div className="space-y-2 mb-5 max-h-[240px] overflow-y-auto pr-1">
                {changes.length === 0 && (
                  <p className="text-content-muted text-[13px]">
                    No dimension shifts this cycle — your profile is holding steady.
                  </p>
                )}
                {changes.map((c) => {
                  const confDelta = c.confidenceAfter - c.confidenceBefore;
                  const scoreDelta = c.newScore - c.previousScore;
                  return (
                    <div
                      key={c.dimension}
                      className="rounded-lg p-3"
                      style={{
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[13px] font-semibold text-white">
                          {c.dimensionDisplayName}
                        </div>
                        <div className="text-[11px] font-code text-content-muted">
                          {scoreDelta !== 0 && (
                            <span className="mr-2">
                              score {scoreDelta > 0 ? '+' : ''}
                              {scoreDelta}
                            </span>
                          )}
                          <span style={{ color: confDelta >= 0 ? '#A39BE0' : '#F0A500' }}>
                            conf {confDelta > 0 ? '+' : ''}
                            {(confDelta * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <p className="text-[12px] text-content-muted leading-snug m-0">
                        {c.reasoning}
                      </p>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={onClose}
                className="w-full rounded-lg font-semibold text-sm py-2.5 text-white"
                style={{ background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)' }}
              >
                <Check size={14} className="inline mr-1" /> Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
